<template>
  <v-container fluid>
    <HeaderRoutinePrint
      data-testid="checklistPage-headerRoutinePrint"
      :routineExecution="routineExecution"
    />
    <DetailRoutinePrint
      data-testid="checklistPage-detailRoutinePrint"
      :routineExecution="routineExecution"
      :checklistItems="visibleChecklistItems"
    />
    <div class="d-flex justify-center">
      <span
        class="pa-0 my-4 body-2 font-weight-bold blue-grey--text text--darken-3"
      >
        {{ $t('checklist.checklist_one') }}
      </span>
    </div>
    <div
      v-for="item in visibleChecklistItems"
      :key="item.id"
      :data-testid="`checklistPage-item-question-${item.id || 'unknown'}`"
    >
      <div v-if="item.type === 'separator'" class="mt-6 mb-3">
        <span class="body-2 font-weight-bold blue-grey--text text--darken-3">
          {{ item.name }}
        </span>
      </div>

      <div v-else>
        <p
          class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 mt-2 mb-1 mr-1"
        >
          {{ `${$t('common.question')}:` }}

          <span class="caption blue-grey--text text--darken-3">
            {{ item.name }}
          </span>
        </p>

        <p
          class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 mr-1 mb-5"
        >
          {{ `${$t('common.answer')}:` }}
          <span
            v-if="item.type !== ITEM_TYPES.IMAGE && item.type !== ITEM_TYPES.SIGNATURE"
            :data-testid="`checklistPage-span-answer-${item.id || 'unknown'}`"
            class="caption blue-grey--text text--darken-3"
          >
            {{ handleResponse(item) }}
          </span>
          <template v-if="item.type === ITEM_TYPES.IMAGE">
            <ImageMediaPrint
              :data-testid="`checklistPage-imageMediaPrint-${item.id || 'unknown'}`"
              v-if="(item.responseImages || []).length === 0"
              :mediaObject="{}"
            />
            <ImageMediaPrint
              :data-testid="`checklistPage-imageMediaPrint-${item.id || 'unknown'}`"
              v-if="(item.responseImages || []).length === 1"
              :mediaObject="(item.responseImages || [])[0]"
            />
            <ImageMediaPrintGrid
              :data-testid="`checklistPage-imageMediaPrintGrid-${item.id || 'unknown'}`"
              v-else-if="(item.responseImages || []).length > 1"
              :mediaObjects="item.responseImages || []"
            />
          </template>
          <template v-if="item.type === ITEM_TYPES.SIGNATURE">
            <ImageMediaPrint
              :data-testid="`checklistPage-imageMediaPrint-signature-${item.id || 'unknown'}`"
              :mediaObject="signatureMediaObject(item)"
            />
            <span class="caption blue-grey--text text--darken-3">
              {{ handleResponse(item) }}
            </span>
          </template>
        </p>
      </div>
    </div>
  </v-container>
</template>

<script>
import { utils } from 'frontend-js-shared'
import userMixin from '@/mixins/userMixin.vue'
import ImageMediaPrint from './ImageMediaPrint.vue'
import ImageMediaPrintGrid from './ImageMediaPrintGrid.vue'
import HeaderRoutinePrint from './HeaderRoutinePrint.vue'
import DetailRoutinePrint from './DetailRoutinePrint.vue'
import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'
  
export default {
  name: 'ChecklistPage',

  mixins: [userMixin],

  components: {
    ImageMediaPrint,
    ImageMediaPrintGrid,
    HeaderRoutinePrint,
    DetailRoutinePrint,
  },

  props: {
    checklistItems: {
      type: Array,
    },
    routineExecution: {
      type: Object,
      required: true,
    },
    totalPages: Number,
  },

  computed: {
    ITEM_TYPES() {
      return ITEM_TYPES
    },

    hasPages() {
      return Boolean(this.totalPages > 0)
    },

    visibleChecklistItems() {
      return this.checklistItems ?? []
    },
  },
  methods: {
    formatDateResponse(item) {
      return item?.includeTime
        ? utils.dates.formatDate(item.response, 'MMM D, YYYY, h:mm A')
        : utils.dates.formatDate(item.response, 'MMM D, YYYY')
    },

    handleResponse(item) {
      switch (item.type) {
        case 'accountSelection': {
          const account = item?.resolvedAccounts?.[0]
          const fullName = this.getFullName(account)
          const email = account?.email ?? ''

          if (fullName && email && fullName !== email) {
            return `${fullName} <${email}>`
          }

          return (
            fullName || email || this.$t('routinesPrint.no_answer_checklist')
          )
        }
        case 'radios':
        case 'select':
          return (
            item?.options?.find(option => option.id === item.response)
              ?.name || this.$t('routinesPrint.no_answer_checklist')
          )
        case 'checkboxes':
          return (
            item?.options
              ?.filter(option => item?.response?.includes(option.id))
              ?.map(item => item?.name)
              ?.join(', ') || this.$t('routinesPrint.no_answer_checklist')
          )
        case 'date':
          return item?.response
            ? this.formatDateResponse(item)
            : this.$t('routinesPrint.no_answer_checklist')
        case 'signature':
          return (
            item?.signature?.signerName ||
            this.$t('routinesPrint.no_answer_checklist')
          )
        default:
          return item?.response ?? this.$t('routinesPrint.no_answer_checklist')
      }
    },

    signatureMediaObject(item) {
      if (item?.signature?.media) {
        return item.signature.media
      }

      if (item?.signature?.mediaUrl) {
        return {
          id: item.signature.mediaId ?? null,
          url: item.signature.mediaUrl,
        }
      }

      if (item?.signature?.mediaId) {
        return {
          id: item.signature.mediaId,
        }
      }

      return {}
    },
  },
}
</script>
<style lang="scss" scoped>
.pages {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  font-size: 10px;
}
</style>
